import PropTypes from "prop-types";
import entityMetaStore from "data/meta/entityMetaStore";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";
import { dehydrateField } from "data/util/hydration";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import TableStore from "data/TableStore";
import NavigatorStore from "data/NavigatorStore";
import TableAccess from "data/util/TableAccess";
import StereotypesStore, { getRecordComparator } from "data/StereotypesStore";
import SuperRecordStore from "data/SuperRecordStore";
import {
    isAllImplementationsSelected,
    isImplementationExpanded,
    isImplementationSelected,
    isVersionSelected,
} from "../NavigatorStore";
import {
    getChildrenByKey,
    navTreeHelperBuilder,
} from "./records";
import EntityName from "data/util/EntityName";
import LiveMap from "data/util/LiveMap";
import { getImplementations } from "data/selectors/stereotypes";
import { recordIdType } from "data/util/typedefs";
import userStore from "data/userStore";
import ClientId from "data/ClientId";

export const TOP_LEVEL_RECORDS_KEY = "topLevelRecords";

export const superRecordPacketType = PropTypes.shape({
    id: recordIdType.isRequired,
    owner: PropTypes.string.isRequired,
    tables: PropTypes.objectOf( // tableKey
        PropTypes.objectOf( // ao_id
            PropTypes.shape({ // record
                _id: recordIdType.isRequired,
                _ts: PropTypes.number.isRequired,
                _cts: PropTypes.number.isRequired,
                // all the dynamic occurrence data by logical field key
            }),
        ),
    ).isRequired,
    topLevelIds: PropTypes.objectOf( // entityKey
        PropTypes.arrayOf(recordIdType), // ao_id[]
    ),
    newIds: PropTypes.objectOf( // clientId
        recordIdType, // ao_id
    ),
});

function srPacketSkeleton(id, owner) {
    return {
        id,
        owner,
        tables: {},
        topLevelIds: {},
        newIds: {},
    };
}

// don't use this, in general.
export const oneOffSuperRecordPacket = (key, id, owner, includeAssocRecords = false) => {
    if (! EntityName.is(key)) {
        // stereotype
        return superRecordPacket(
            TableStore,
            StereotypesStore.getStereotypeByKey(key),
            id,
            SuperRecordStore.getRecord(key, id),
            owner,
            includeAssocRecords,
        );
    }
    // entity
    const packet = srPacketSkeleton(id, owner);
    const addTableRecord = addTableRecordBuilder(
        TableStore,
        packet.tables,
        includeAssocRecords
    );
    addTableRecord(key, id);
    return packet;
};

const addTableRecordBuilder = (TableStore, tables, includeAssocRecords) => {
    // there's likely to be a lot of repeats, so locally-cache the lookups
    const tkc = new LiveMap(ek =>
        entityMetaStore.getInfo(ek).tableKey);
    const cic = new LiveMap(tk =>
        TableColumnMetaStore.getAllColumns(tk)
            .filter(it => !it.inverse));
    const cfic = new LiveMap(tk =>
        entityMetaStore.getInfosForLogicalTable(tk)
            .reduce((cols, ei) =>
                entityFieldMetaStore.getAllInfos(ei.key, info =>
                    info.type === "CompositionField" || (includeAssocRecords && info.type === "AssociationField"),
                )
                    .reduce((cols, fi) => ({
                        ...cols,
                        [fi.logicalColumn]: fi.otherEntityKey,
                    }), cols), {}));

    const addTableRecord = (ek, id) => {
        const tableKey = tkc.get(ek);
        const colInfos = cic.get(tableKey);
        const compMap = cfic.get(tableKey);
        if (!tables.hasOwnProperty(tableKey)) {
            tables[tableKey] = {};
        }
        const table = tables[tableKey];
        if (table.hasOwnProperty(id)) {
            return;
        }
        const rd = TableStore.getRecordData(tableKey, id);
        table[id] = {
            _id: rd.meta.id,
        };
        if (rd.meta.ts) {
            table[id]._ts = rd.meta.ts;
        }
        for (const ci of colInfos) {
            table[id][ci.name] = dehydrateField(rd.data[ci.name], ci.type);
        }
        for (const lc of Object.keys(compMap)) {
            let cids = table[id][lc];
            if (cids == null) continue;
            if (Array.isArray(cids)) {
                if (cids.length === 0) continue;
            } else {
                cids = [cids];
            }
            for (const cid of cids) {
                addTableRecord(compMap[lc], cid);
            }
        }
    };
    return addTableRecord;
};

export const superRecordPacket = (TableStore, stereoInfo, id, srInfo, owner, includeAssocRecords = false) => {
    if (stereoInfo == null) {
        throw new Error("Can't build a superRecord packet w/ null stereotype info!");
    }
    const packet = {
        ...srPacketSkeleton(id, owner),
        topLevelIds: srInfo.topLevelIds,
    };
    const addTableRecord = addTableRecordBuilder(
        TableStore,
        packet.tables,
        includeAssocRecords
    );

    const access = new TableAccess(TableStore.getState());
    const stereoImplementations = getImplementations(stereoInfo, access, id);
    // doesn't matter what implementation this is - they all become the same tableKey
    addTableRecord(stereoImplementations[0].key, id);
    tlrBuilder(TableStore, srInfo)(addTableRecord);
    return packet;
};

const tlrBuilder = (TableStore, srInfo) => keyAndIdWork =>
    Object.keys(srInfo.topLevelIds).forEach(ek =>
        srInfo.topLevelIds[ek].forEach(id =>
            keyAndIdWork(ek, id)));

export const getTopLevelRecords = (TableStore, srInfo) => {
    const result = [];
    tlrBuilder(TableStore, srInfo)((ek, id) =>
        result.push(TableStore.getEntityProxy(ek, id)));
    return result;
};



// Find the nearest navigable parent of the given key:id:shortname triple,
// ideally itself. Return the resulting triple, plus a flag for whether a
// non-navigable edge was chopped off.
export const nearestNavigableParent = (
    TableStore,
    srInfo,
    key,
    id,
    shortname,
) => {
    const versionKey = new EntityName(key).versionKey;
    const candidateTLRs = getTopLevelRecords(TableStore, srInfo)
        .filter(it => new EntityName(it.entityKey).versionKey === versionKey);

    // i return the path to the record, or null if it can't be found
    const findPath = (record, includeAssoc) => {
        if (record.id === id && record.entityKey === key) return {record};
        const cbk = getChildrenByKey(record, includeAssoc);
        for (const fk of Object.keys(cbk)) {
            const v = cbk[fk];
            const info = entityFieldMetaStore.getInfo(record.entityKey, fk);
            for (const r of (v instanceof Array ? v : [v])) {
                const child = findPath(r, includeAssoc);
                if (child != null) {
                    return {
                        record,
                        child: {
                            ...child,
                            info,
                        },
                    };
                }
            }
        }
        return null;
    };

    let path;
    let usedAssoc = false;
    for (const tlr of candidateTLRs) {
        path = findPath(tlr, false);
        if (path != null) break;
    }
    if (path == null) {
        for (const tlr of candidateTLRs) {
            path = findPath(tlr, true);
            if (path != null) {
                usedAssoc = true;
                break;
            }
        }
    }
    if (path != null) {
        let me = path;
        while (me.child && me.child.info) {
            if( ! (me.child.info.mrPointer || (me.child.info.type === "CompositionField" && me.child.info.navigable)) ) {
                break;
            }
            me = me.child;
        }
        return [me.record.entityKey, me.record.id, (me.child && me.child.info.key) || shortname, usedAssoc];
    }
    console.error("couldn't find a navigable parent...", key, id, shortname);
    return [key, id, shortname, false];
};

export const superNavTree = (
    TableStore,
    srInfo,
    navState,
    key,
    id,
    ctx = null,
) => {
    const rootInfo = StereotypesStore.getStereotypeByKey(key);
    const helper = navTreeHelperBuilder(navState);
    const rootAccess = new TableAccess(TableStore.getState());
    const rootImplementations = getImplementations(rootInfo, rootAccess, id);
    const sp = TableStore.getStereotypeProxy(key, id);
    let rs;
    if (ctx != null) rs = NavigatorStore.getRecordState(ctx);
    if (rs != null) sp.withEntityContext(rs.key);
    const tree = {
        id,
        fields: [],
        childCount: 0,
        label: sp.getLabel(),
        detail: sp.getDetailString(),
        supportsEnablement: sp.stereoInfo.supportsEnablement,
        isMagic: rootInfo.magicRoot,
        name: rootInfo.name,
        key: key,
        stereoKey: key,
        selected: isAllImplementationsSelected(navState),
    };
    let activePrefix;
    tree.projections = Object.values(rootImplementations.reduce((ps, it) => {
        if (! ps.hasOwnProperty(it.implementationKey)) {
            const selected = isImplementationSelected(
                navState,
                it.implementationKey,
            );
            if (selected) activePrefix = it.implementationKey;
            ps[it.implementationKey] = {
                implementationKey: it.implementationKey,
                implementationName: it.implementationName,
                selected,
                expanded: isImplementationExpanded(
                    navState,
                    it.implementationKey,
                ),
                versions: [],
            };
        }
        const selected = isVersionSelected(navState, it.key);
        const recTree = helper(TableStore.getEntityProxy(
            it.key,
            id,
        ));
        const v = {
            versionKey: it.versionKey,
            versionName: it.versionName,
            key: it.key,
            name: it.entityName,
            selected,
            childCount: recTree.childCount,
        };
        if (selected) {
            activePrefix = it.versionKey;
            tree.fields.push(...recTree.fields);
            tree.childCount += recTree.childCount;
        }
        ps[it.implementationKey].versions.push(v);
        return ps;
    }, {}));
    if (tree.isMagic) {
        const topLevelRecords = getTopLevelRecords(TableStore, srInfo);
        const childInfo = StereotypesStore.getStereotypeByKey(rootInfo.magicChildKey);
        tree.fields.push({
            key: TOP_LEVEL_RECORDS_KEY,
            recordKey: key,
            recordId: id,
            name: childInfo.pluralName,
            creatables: getSuperRecordCreatables(childInfo),
            allowDelete: true,
            items: getSuperRecordItems(topLevelRecords, childInfo)
                .filter(it =>
                    activePrefix == null || it.entityKey.startsWith(activePrefix + "/"))
                .map(it => helper(it)),
        });
        tree.childCount = topLevelRecords.length;
        tree.projections.forEach(p => {
            p.childCount = topLevelRecords
                .filter(it =>
                    it.entityKey.startsWith(p.implementationKey + "/"))
                .length;
            p.versions.forEach(v => {
                v.childCount += topLevelRecords
                    .filter(it =>
                        it.entityKey.startsWith(v.versionKey + "/"))
                    .length;
            });
        });
    }
    return tree;
};

export const getSuperSelectedVersion = (item) => {
    // return first selected version found or null.
    if(!item.isMagic || !item.hasOwnProperty("projections")) {
        console.log("Expected item to be super record with projections");
        return null;
    }
    for(const p of item.projections) {
        for(const v of p.versions) {
            if(!v.selected) continue;
            return v;
        }
    }
    return null;
};

export const getSuperRecordCreatables = (sr) =>
    sr.implementations
        .filter(it => it.creatable)
        .map(it => ({
            // this needs to be the magic children
            key: it.key,
            name: it.implementationName + " v" + it.versionName,
            implKey: it.implementationKey,
        }));

export const getSuperRecordItems = (topLevelRecords, childInfo) =>
    topLevelRecords
        .sort(getRecordComparator(childInfo));

export const getActiveRecordSiblings = (fluxContext, activeRecord, extraFilter = null) => {
    const entityInfo = activeRecord.entityInfo;
    let items = [];
    switch(entityInfo.type) {
        case "GLOBAL":
            // this list could be to large and should requires a search modal.
            // Just return empty array.
            return [];
        case "ROOT":
            const magic = NavigatorStore.getMagicState(fluxContext);
            const srInfo = SuperRecordStore.getRecord(magic.key, magic.id);
            items = getTopLevelRecords(TableStore, srInfo);
            break;
        case "CHILD":
            // use parentPointerKey to get parent then look for siblings
            if(!entityInfo.parentPointerKey) {
                throw new Error("expecting parentPointerKey to be defined");
            }
            const ppInfo = entityFieldMetaStore.getInfo(entityInfo.key, entityInfo.parentPointerKey);
            const parentEP = activeRecord.get(ppInfo.key);
            if(parentEP === null) {
                // the activeRecord has been orphaned... therefore no siblings
                return [];
            }
            items = parentEP.get(ppInfo.inverseKey) || []; // aways return array
            break;
        default:
            throw new Error(`Unknown activeRecord type`);
    }
    return items.filter(
        it => !it.equals(activeRecord) && (typeof extraFilter === "function" ? extraFilter(it) : true)
    );
};

export const canDeleteSuperRecord = sp => {
    if (sp.stereoInfo.magicRoot) {
        // every magic child must be deletable as well
        const srLO = SuperRecordStore.getRecordLO(sp.stereoKey, sp.id);
        if (!srLO.hasValue()) return false;
        const tlrs = getTopLevelRecords(TableStore, srLO.getValueEnforcing());
        if (tlrs.some(it => !it.canDelete())) {
            return false;
        }
    } else if (sp.stereoInfo.supportsEnablement) {
        // using enablement, so never deletable
        return false;
    }
    // If can delete any implementation, good enough
    return getImplementations(
        sp.stereoInfo,
        new TableAccess(TableStore.getState()),
        sp.id
    ).some(it => entityMetaStore.getInfo(it.key).canDelete);
};

export const canMergeSuperRecord = sp => {
    if (ClientId.is(sp.id)) return false;
    const partiallyNormal = getImplementations(
        sp.stereoInfo,
        new TableAccess(TableStore.getState()),
        sp.id
    ).some(it => !entityMetaStore.getInfo(it.key).lookup);
    return partiallyNormal
        ? userStore.canMergeMagicRoots()
        : userStore.canMergeLookups();
};


export const rerouteFieldNav = (magic, entityKey, recordId, fieldKey) => {
    const fieldSpec = {
        entityKey: entityKey,
        recordId: recordId,
        fieldKey: fieldKey,
    };
    let assocInPlay;
    // eslint-disable-next-line prefer-const
    [entityKey, recordId, fieldKey, assocInPlay] = nearestNavigableParent(
        TableStore,
        SuperRecordStore.getRecord(magic.key, magic.id),
        entityKey,
        recordId,
        fieldKey,
    );
    if (recordId == null || entityKey == null) throw new Error("how/why did this happen?");
    if (assocInPlay) {
        fieldSpec.entityKey = entityKey;
        fieldSpec.recordId = recordId;
        fieldSpec.fieldKey = fieldKey;
    }
    return [entityKey, recordId, fieldSpec];
};



// WEBPACK FOOTER //
// ./app/data/selectors/superRecords.js